// ============================================================================
// TAGGED LIST EDITOR
// Reusable editor for CognitiveElement[] fields (lenses, mandatory questions,
// evidence rules, decision criteria) — each entry is a short text plus
// optional comma-separated topic tags that gate when it activates in a
// conversation turn. Sibling to StringListEditor, same interaction shape.
// ============================================================================

import React, { useState } from 'react';
import { X } from 'lucide-react';
import { CognitiveElement } from '../../types';

interface TaggedListEditorProps {
  label: string;
  items: CognitiveElement[];
  onChange: (items: CognitiveElement[]) => void;
  textPlaceholder?: string;
  topicsPlaceholder?: string;
  hint?: string;
}

function parseTopics(raw: string): string[] {
  return raw
    .split(',')
    .map(t => t.trim().toLowerCase())
    .filter(Boolean);
}

export default function TaggedListEditor({
  label,
  items,
  onChange,
  textPlaceholder = 'Enter text...',
  topicsPlaceholder = 'Topics (comma-separated, optional — blank = always active)',
  hint
}: TaggedListEditorProps) {
  const [textInput, setTextInput] = useState('');
  const [topicsInput, setTopicsInput] = useState('');

  const handleAdd = () => {
    const trimmed = textInput.trim();
    if (!trimmed) return;
    const element: CognitiveElement = {
      id: crypto.randomUUID(),
      text: trimmed,
      topics: parseTopics(topicsInput)
    };
    onChange([...items, element]);
    setTextInput('');
    setTopicsInput('');
  };

  const handleRemove = (id: string) => {
    onChange(items.filter(i => i.id !== id));
  };

  return (
    <div>
      <label className="block text-sm font-semibold text-text-main mb-2">{label}</label>
      <div className="flex flex-col md:flex-row gap-2 mb-3">
        <input
          type="text"
          value={textInput}
          onChange={(e) => setTextInput(e.target.value)}
          placeholder={textPlaceholder}
          className="flex-1 px-4 py-3 rounded-xl border border-slate-200 text-sm text-text-main focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all placeholder:text-slate-400"
        />
        <input
          type="text"
          value={topicsInput}
          onChange={(e) => setTopicsInput(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAdd())}
          placeholder={topicsPlaceholder}
          className="md:w-72 px-4 py-3 rounded-xl border border-slate-200 text-sm text-text-main focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all placeholder:text-slate-400"
        />
        <button
          onClick={handleAdd}
          type="button"
          className="px-5 py-3 bg-navy text-white rounded-xl text-sm font-semibold hover:bg-navy-dark transition-colors whitespace-nowrap"
        >
          Add
        </button>
      </div>
      <div className="flex flex-col gap-2">
        {items.map((item) => (
          <div
            key={item.id}
            className="flex items-start justify-between gap-3 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl"
          >
            <div className="min-w-0">
              <p className="text-sm text-text-main">{item.text}</p>
              {item.topics.length > 0 ? (
                <div className="flex gap-1.5 flex-wrap mt-1.5">
                  {item.topics.map((topic) => (
                    <span
                      key={topic}
                      className="px-2 py-0.5 bg-primary/20 text-text-main rounded-md text-[11px] font-semibold"
                    >
                      {topic}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-[11px] text-text-sub mt-1">Always active (no topic gate)</p>
              )}
            </div>
            <button
              onClick={() => handleRemove(item.id)}
              type="button"
              className="text-text-main/60 hover:text-text-main transition-colors flex-shrink-0"
            >
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
      {hint && <p className="text-xs text-text-sub mt-1.5">{hint}</p>}
    </div>
  );
}
